/** @format */
import { css } from 'styled-components';

import { largeDesktop, largeTablet } from '@toucan/client/global/application';

import { quietTextHighlight, quietTextSmall } from '../../../typography';
import { StyledFanCardProps } from './config/types';

/* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
export const styles = ({ $angle, $isSelected }: StyledFanCardProps) => css`
    /*
     * On desktop every card is the same fixed size, whatever its model is
     * called: cards that sized themselves to their content would give each
     * card in the hand a different width and height, and the fan would stop
     * reading as one hand of playing cards.
     */
    --journey-fan-card__width: 12rem;
    --journey-fan-card__image-height: 12.8rem;

    /*
     * Stack every card in the hand into the same grid cell.
     *
     * This element deliberately carries no transform, position or z-index of
     * its own: any of those would make it a stacking context, which would trap
     * each button's z-index inside its own card and stop a hovered or focused
     * card rising above its neighbors.
     */
    grid-area: 1 / 1;
    list-style: none;
    min-width: 0;

    /*
     * The button, not the list item, carries the rotation. It is the element
     * that can be hovered and focused, so keeping the transform here means
     * :focus-visible can drive the lift directly rather than through a
     * :has() on the ancestor — which jsdom cannot parse, and which would take
     * every test that clicks a card down with it.
     */
    .fan-card-button {
        appearance: none;
        background: none;
        border: 0;
        margin: 0;
        padding: 0;
        cursor: pointer;
        width: var(--journey-fan-card__width);
        /*
         * Fills the cell every card in the hand shares, so all five come out the
         * height of the tallest rather than each sizing to its own text. Without it
         * a model whose tagline wraps to one more line than its neighbor's makes a
         * visibly taller card, and how many lines a tagline takes changes with the
         * width of the page.
         */
        height: 100%;
        text-align: left;

        /*
         * The rotation pivots about a point far BELOW the card, the way a hand
         * of playing cards pivots about the fingers holding it. The tilt stays
         * gentle so the names stay readable; how far apart the cards spread is
         * set by the distance to that pivot instead.
         */
        transform-origin: 50% var(--journey-fan__pivot-y, 320%);
        transform: rotate(${$angle}deg);

        /* Needed for z-index to apply, and to let a lifted card rise above its
        neighbors rather than only within its own list item. */
        position: relative;
        z-index: 1;

        transition-property: transform, box-shadow;
        transition-duration: var(--animations-duration-normal);
        transition-timing-function: var(--animations-easing-ease-out);

        &:hover,
        &:focus-visible {
            /*
             * translateY AFTER rotate moves the card along its own rotated
             * axis rather than the page's vertical axis — that is what reads
             * as drawing the card out of the hand. Keep this order.
             *
             * The lift is signed by the side the hand is on, because "out of
             * the hand" means away from the axis: the hand above the line
             * draws upward, the one below it draws downward. The section sets
             * the sign per side.
             */
            transform: rotate(${$angle}deg)
                translateY(var(--journey-fan__lift, -1.6rem));
            z-index: 2;
        }

        &:focus-visible {
            /*
             * Cards overlap, so an un-raised, un-outlined focus ring would be
             * painted over by the next card in DOM order. Both the outline
             * and the raised z-index are load-bearing for keyboard users.
             */
            outline: 0.2rem solid var(--colors-accessibility);
            outline-offset: 0.2rem;
            border-radius: 0.8rem;
        }
    }

    /* Passes the button's full height on, so the visible card is what stretches. */
    .fan-card-surface {
        width: 100%;
        height: 100%;
        padding: var(--layout-spacing-12);
    }

    .fan-card-content {
        /* Needs position: relative to sit above CardBackground's
        absolutely-positioned .background element. */
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--layout-spacing-8);
        width: 100%;
        min-width: 0;
    }

    /*
     * The box never grows or shrinks to suit its image, so that a portrait
     * lens shot and a landscape body shot leave the card exactly the same
     * size.
     */
    .fan-card-thumbnail {
        width: 100%;
        flex: 0 0 auto;
        background-color: var(--colors-surface-secondary);
        border-radius: var(--effects-radius-sm);
        overflow: hidden;
    }

    /*
     * The image fills the box and is cropped to it rather than being fitted
     * inside it, so no strip of empty box is ever left showing above and
     * below a landscape image.
     *
     * Scoped through the content wrapper to outrank the shared Thumbnail's
     * own contain, which it sets at the same specificity as a bare img rule.
     */
    .fan-card-content .fan-card-thumbnail img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    /*
     * Two lines are reserved but not enforced, so a one-word model name cannot make
     * its card shorter than the card beside it while a long one is still allowed to
     * run on rather than being cut off mid-line.
     *
     * Nothing is clipped here. The tagline is cut to length in the card itself
     * (CARD_TAGLINE_LIMIT), where it can be given an ellipsis that says it was cut;
     * a clip in the stylesheet would just make text vanish at whatever width the
     * card happened to be.
     *
     * Cards in a hand share one grid cell and stretch to fill it, so the tallest
     * card in a hand sets the height of all of them either way.
     */
    .fan-card-name {
        ${quietTextHighlight}
        color: var(--colors-text-secondary);
        text-align: center;
        overflow-wrap: anywhere;
        line-height: 1.4;
        min-height: 2.8em;
    }

    .fan-card-description {
        ${quietTextSmall}
        color: var(--colors-text-tertiary);
        text-align: center;
        overflow-wrap: anywhere;
        line-height: 1.4;
        min-height: 2.8em;
        /*
         * Pulled up out of the content's flex gap, which spaces the name from the
         * image above it and is too much between the name and this: the two read as
         * one label for the card, not as two things. The card keeps its size — the
         * room this frees goes to the tagline, which is the part with a length limit
         * to fit into.
         */
        margin-top: calc(var(--layout-spacing-4) * -1);
    }

    ${
        $isSelected &&
        css`
            /*
         * Drawn further out than hover and pinned there — this is the state
         * the detail card below is currently showing.
         *
         * The hover and focus states are repeated here rather than left to the
         * resting rule, which would lose to their higher specificity —
         * hovering the card you had already picked would push it back down.
         */
            .fan-card-button,
            .fan-card-button:hover,
            .fan-card-button:focus-visible {
                transform: rotate(${$angle}deg)
                    translateY(var(--journey-fan__lift-selected, -2.4rem));
                z-index: 3;
            }

            // The ring goes on CardBackground's own surface element, and as a
            // shadow rather than a border so it costs no layout and cannot shift
            // the axis.
            .fan-card-surface .background {
                box-shadow:
                    0 0 0 0.2rem var(--colors-brand-secondary),
                    var(--effects-shadow-lg);
            }
        `
    }

    ${largeDesktop(css`
        --journey-fan-card__width: 14rem;
    `)}

    /*
     * Below large tablet the fan is abandoned entirely and the cards become an
     * ordinary list. A fan needs horizontal room to splay into and a phone has
     * none:
     * held on to, it would either overlap its own cards into illegibility or
     * shrink them past the point of being tappable.
     *
     * So the rotation, the lift and the stacking all come off, and the card
     * lies on its side and fills the row — it is no longer competing for
     * width with four siblings, and no longer competing for height either.
     */
    ${largeTablet(css`
        --journey-fan-card__image-height: 6.4rem;

        /*
         * Out of the shared cell and into a row of its own, split into the
         * axis column and the card beside it.
         */
        grid-area: auto;
        position: relative;
        display: grid;
        grid-template-columns:
            var(--journey-timeline__axis-size, 5.6rem)
            minmax(0, 1fr);
        align-items: center;

        /*
         * The axis is drawn per card rather than as one line down the section,
         * because stacked cards of differing heights mean there is no midpoint
         * to measure a single line from. Each segment overreaches by half the
         * list's row gap so it joins up with the next one. Nothing is drawn
         * against the marker, which is what leaves its circle in a clear break
         * rather than with a line running through it.
         */
        &::before {
            content: '';
            position: absolute;
            top: calc(var(--layout-spacing-16) / -2);
            bottom: calc(var(--layout-spacing-16) / -2);
            left: calc(var(--journey-timeline__axis-size, 5.6rem) / 2);
            width: 0.2rem;
            transform: translateX(-50%);
            background-color: var(--journey-fan__axis-colour, currentColor);
        }

        /* The stop's dot, sitting on the axis in the column's centre. */
        &::after {
            content: '';
            grid-column: 1;
            grid-row: 1;
            justify-self: center;
            /* Paints above the line rather than under it. */
            position: relative;
            width: 1.2rem;
            height: 1.2rem;
            border-radius: var(--effects-radius-full);
            background-color: var(--colors-brand-primary);
            /* A ring of page colour so the dot reads as sitting on the axis. */
            box-shadow: 0 0 0 0.4rem var(--colors-surface-primary);
        }

        .fan-card-button {
            grid-column: 2;
            grid-row: 1;
            width: 100%;
            /* A card is its own row here, so there is nothing to equalize against. */
            height: auto;
            transform: none;
            z-index: auto;

            &:hover,
            &:focus-visible {
                transform: none;
                z-index: auto;
            }
        }

        .fan-card-content {
            flex-direction: row;
            align-items: center;
            text-align: left;
        }

        .fan-card-thumbnail {
            width: 8rem;
        }

        /* A row is as tall as it needs to be, so nothing has to be reserved. */
        .fan-card-name {
            text-align: left;
            min-height: 0;
        }

        .fan-card-description {
            text-align: left;
            min-height: 0;
            /* The card is on its side here and has the room, so it keeps the gap. */
            margin-top: 0;
        }
    `)}

    /*
     * No reduced-motion helper exists in this codebase (verified) — this is a
     * raw @media block, following the precedent at
     * intro-screen-images.styles.ts:170. The transforms themselves stay: the
     * lift is how selection is communicated, not decoration, only the
     * animation between states goes away.
     */
    @media (prefers-reduced-motion: reduce) {
        .fan-card-button {
            transition-duration: var(--animations-duration-instant);
        }
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
